/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { Fragment, useEffect, useState } from "react";
import {} from "./queries";
import { Table, Icon, Grid } from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import {
  getUrlPath,
  resetUrlParams,
  setUrlParam,
  STATE_ERROR,
  STATE_NO_BUILDS,
  STATE_PENDING_SUPPORT,
  STATE_UPLOAD_FAILED,
  STATE_CANCELLED,
  STATE_DONE,
  STATE_EXPIRED,
  STATE_IN_PROGRESS,
  STATE_QUEUE,
  STATE_START,
  STATE_UPLOADED
} from "../../Authenticator/utils";
import BuildPopup from "./BuildPopup";
import { Data } from "../../../types/MessageDialogParams";
import { StateIcon } from "../../Icon/StateIcon";

export const getBuildTitle = (state: string) => {
  const buildStates: { [key: string]: any } = {
    [STATE_START]: "Created",
    [STATE_UPLOADED]: "Ready to protect",
    [STATE_UPLOAD_FAILED]: "Upload failed",
    [STATE_CANCELLED]: "Protection cancelled",
    [STATE_QUEUE]: "Pending",
    [STATE_IN_PROGRESS]: "Protecting",
    [STATE_PENDING_SUPPORT]: "Validation Pending",
    [STATE_ERROR]: "Failed",
    [STATE_DONE]: "Protected",
    [STATE_EXPIRED]: "Expired",
    [STATE_NO_BUILDS]: "No builds",
  };
  return buildStates[state] || STATE_UPLOADED;
};


interface BuildsListProps {
  buildsData: any;
  findBuildId: string | null;
  setFindBuildId: any;
  setMessage: any;
}

const BuildsList: React.FC<BuildsListProps> = ({
  buildsData,
  findBuildId,
  setFindBuildId,
  setMessage,
}) => {
  const [sorting, setSorting] = useState<any>({
    sortBy: "name",
    reverse: false,
  });
  const [showBuildPopup, setShowBuildPopup] = useState<Data | null>(null);
  const [lastActiveBuild, setLastActiveBuild] = useState<Data | null>(null);

  useEffect(() => {
    if (findBuildId && findBuildId !== showBuildPopup?.id) {
      setShowBuildPopup({ id: findBuildId });
    }
  }, [findBuildId]);

  const getOsInfo = (build: any) => {
    if (!build.osInfo) {
      return "";
    }

    if (build.os === "android") {
      const { minSdkVersion, targetSdkVersion } = build.osInfo;
      return (
        <Fragment>
          {targetSdkVersion && <div>{`TargetSdk ${targetSdkVersion}`}</div>}
          {minSdkVersion && <div>{`MinSdk ${minSdkVersion}`}</div>}
        </Fragment>
      );
    }

    const { XcodeVersion } = build.osInfo;
    return XcodeVersion ? `Xcode ${build.osInfo.XcodeVersion}` : "";
  };

  const getBuildRow = (build: any, idx: number) => {
    return (
      <Table.Row
        active={
          (showBuildPopup?.id === build.id &&
            showBuildPopup?.updated === build.updated) ||
          (lastActiveBuild?.id === build.id &&
            lastActiveBuild?.updated === build.updated)
        }
        key={idx}
        style={{ cursor: "pointer" }}
        error={build.state === STATE_ERROR}
        warning={[STATE_PENDING_SUPPORT, STATE_UPLOAD_FAILED].includes(
          build.state
        )}
        onClick={() => {
          if (!window.getSelection()?.toString().length) {
            setShowBuildPopup(build);
            setLastActiveBuild(build);
            if (getUrlPath().endsWith("Builds")) {
              setUrlParam("b", build.id);
            }
          }
        }}
      >
        <Table.Cell className={styles.wordWrap}>
          {build.updated &&
            new Date(build.updated).toLocaleDateString(navigator.language, {
              year: "numeric",
              month: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
            })}
        </Table.Cell>

        <Table.Cell>{build.orgName}</Table.Cell>

        <Table.Cell>{build.id}</Table.Cell>

        <Table.Cell>{build.applicationPackageId}</Table.Cell>

        <Table.Cell className={styles.wordWrap}>
          {build.os === "android" ? "Android" : "iOS"}
        </Table.Cell>

        <Table.Cell className={styles.wordWrap}>{getOsInfo(build)}</Table.Cell>

        <Table.Cell>{build.errorMessage}</Table.Cell>

        <Table.Cell>{build.vmxPlatform && "Platform"}</Table.Cell>

        <Table.Cell className={styles.wordWrap}>
          <div style={{ whiteSpace: "nowrap", display: "flex" }}>
            <StateIcon
              name={build.state || STATE_NO_BUILDS}
              style={{ width: "18px", height: "18px", marginRight: "5px" }}
            />
            {getBuildTitle(build.state)}
          </div>
        </Table.Cell>
      </Table.Row>
    );
  };

  const sortBuilds = (valueA: any, valueB: any) => {
    let sortValueA, sortValueB;

    switch (sorting.sortBy) {
      case "buildId":
        sortValueA = valueA.id;
        sortValueB = valueB.id;
        break;

      case "os":
        sortValueA = valueA.os;
        sortValueB = valueB.os;
        break;

      case "osInfo":
        sortValueA =
          valueA.os === "android"
            ? `Target ${valueA.osInfo?.targetSdkVersion}`
            : `Xcode ${valueA.osInfo?.XcodeVersion}`;
        sortValueB =
          valueB.os === "android"
            ? `Target ${valueB.osInfo?.targetSdkVersion}`
            : `Xcode ${valueB.osInfo?.XcodeVersion}`;
        break;

      case "org":
        sortValueA = valueA.orgName;
        sortValueB = valueB.orgName;
        break;

      case "packageId":
        sortValueA = valueA.applicationPackageId;
        sortValueB = valueB.applicationPackageId;
        break;

      case "state":
        sortValueA = valueA.state;
        sortValueB = valueB.state;
        break;

      case "error":
        sortValueA = valueA.errorMessage;
        sortValueB = valueB.errorMessage;
        break;

      case "platform":
        sortValueA = valueA.vmxPlatform;
        sortValueB = valueB.vmxPlatform;
        break;

      default:
        sortValueA = valueA.updated;
        sortValueB = valueB.updated;
        break;
    }

    if (!sortValueA) {
      sortValueA = "";
    }
    if (!sortValueB) {
      sortValueB = "";
    }

    if (typeof sortValueA === "string") {
      sortValueA = sortValueA.toLocaleLowerCase();
    }
    if (typeof sortValueB === "string") {
      sortValueB = sortValueB.toLocaleLowerCase();
    }

    if (sortValueA < sortValueB) {
      return sorting.reverse ? 1 : -1;
    } else if (sortValueA > sortValueB) {
      return sorting.reverse ? -1 : 1;
    }
    return 0;
  };

  const setBuildSorting = (sortBy: string) => {
    setSorting({
      sortBy: sortBy,
      reverse: sortBy === sorting.sortBy ? !sorting.reverse : false,
    });
  };

  const getSortIndicator = (columnName: string) => {
    if (sorting.sortBy === columnName) {
      return (
        <Icon
          name={sorting.reverse ? "triangle down" : "triangle up"}
          style={{ marginLeft: "3px" }}
        />
      );
    }
    return null;
  };

  const closeBuildPopup = () => {
    setShowBuildPopup(null);
    setFindBuildId(null);
    resetUrlParams();
  };

  if (buildsData && buildsData.length > 0) {
    const sortedBuilds = buildsData.sort((valueA: any, valueB: any) =>
      sortBuilds(valueA, valueB)
    );

    return (
      <Fragment>
        <Table
          selectable
          sortable
          celled
          compact="very"
          className={styles.usersTable}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                onClick={() => {
                  setBuildSorting("updated");
                }}
              >
                Updated
                {getSortIndicator("updated")}
              </Table.HeaderCell>

              <Table.HeaderCell
                onClick={() => {
                  setBuildSorting("org");
                }}
              >
                Organization
                {getSortIndicator("org")}
              </Table.HeaderCell>

              <Table.HeaderCell
                onClick={() => {
                  setBuildSorting("buildId");
                }}
              >
                Build ID
                {getSortIndicator("buildId")}
              </Table.HeaderCell>

              <Table.HeaderCell
                onClick={() => {
                  setBuildSorting("packageId");
                }}
              >
                Package ID
                {getSortIndicator("packageId")}
              </Table.HeaderCell>

              <Table.HeaderCell
                onClick={() => {
                  setBuildSorting("os");
                }}
              >
                OS
                {getSortIndicator("os")}
              </Table.HeaderCell>

              <Table.HeaderCell
                onClick={() => {
                  setBuildSorting("osInfo");
                }}
              >
                OS Info
                {getSortIndicator("osInfo")}
              </Table.HeaderCell>

              <Table.HeaderCell
                onClick={() => {
                  setBuildSorting("error");
                }}
              >
                Error
                {getSortIndicator("error")}
              </Table.HeaderCell>

              <Table.HeaderCell
                onClick={() => {
                  setBuildSorting("platform");
                }}
              >
                Platform
                {getSortIndicator("platform")}
              </Table.HeaderCell>

              <Table.HeaderCell
                onClick={() => {
                  setBuildSorting("state");
                }}
              >
                State
                {getSortIndicator("state")}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {sortedBuilds.map((build: any, idx: number) =>
              getBuildRow(build, idx)
            )}
          </Table.Body>
        </Table>

        {showBuildPopup && (
          <BuildPopup
            build={showBuildPopup}
            onClose={closeBuildPopup}
            setMessage={setMessage}
          />
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Grid>
        <Grid.Column textAlign="center">
          <div className={styles.myAccountNoUsersLabel}>
            {(buildsData && "No builds found.") || ""}
          </div>
        </Grid.Column>
      </Grid>

      {showBuildPopup && (
        <BuildPopup
          build={showBuildPopup}
          onClose={closeBuildPopup}
          setMessage={setMessage}
        />
      )}
    </Fragment>
  );
};

export default BuildsList;
